import { resolveRowUnitType } from '@/hooks';
import {
  buildOutgoingShipmentDiscrepancySections,
  buildDeadlineWarning,
  buildLinkedGroups,
  getLinkedGroup,
} from '@/components/common';
import { formValue } from '@/lib/mappers/formValues';
import { useGetLinkableOutgoingShipmentsForOrderQuery } from '../api/ordersApi';
import {
  buildSelectOutgoingShipmentColumns,
  buildSelectedOutgoingShipmentColumns,
} from './selectOutgoingShipmentColumns';
import { buildUnitHeaderOptions } from './orderUnits';

// Orders ▸ Fulfil ▸ Select outgoing shipment — config for <SelectFromTable>.

const buildDiscrepancy = (t, row, unitType) => {
  return {
    referenceId: row.shipmentId,
    message: buildDeadlineWarning(t, {
      dateOfDispatch: row.dateOfDispatchRaw,
      pendingQuantity: row.rawByUnit?.[unitType]?.remainingToAllocateQuantity,
      unit: row.byUnit?.[unitType]?.unit,
    }),
    sections: buildOutgoingShipmentDiscrepancySections(t, row, unitType),
  };
};

// The order's own material/site/customer decide which shipments can carry it.
const useCandidates = ({ context, enabled }) => {
  const { record, certificateType } = context ?? {};
  const { data, isFetching } = useGetLinkableOutgoingShipmentsForOrderQuery(
    { certificateType, id: record?.id, match: record?.linkableMatch },
    { skip: !certificateType || !record?.linkableMatch || !enabled },
  );
  return { rows: data, total: data?.length ?? 0, loading: isFetching };
};

// Shipments already linked to this order first, then every other candidate.
const buildGroups = (t) =>
  buildLinkedGroups(t, {
    linkedKey: 'FIELD_LINKED_SHIPMENTS',
    otherKey: 'PICKER_GROUP_OTHER_SHIPMENTS',
  });

// One shipment per fulfilment; the form's locked fields all come off the picked row.
const writeSelection = ({ selected, selections, setValue }) => {
  const row = selected[0];
  if (!row) {
    [
      'existingShipmentId',
      'idNumber',
      'shipmentQuantity',
      'shipmentQuantityUnit',
      'dateOfDispatch',
      'vesselName',
      'transportName',
    ].forEach((key) => setValue(key, ''));
    setValue('idPrefix', 'SP');
    return;
  }
  const unitType = resolveRowUnitType(row, selections);
  const unit = row.byUnit?.[unitType];
  setValue('existingShipmentId', row.id);
  // A picked record keeps its own id whole, same as editing a saved contract id.
  setValue('idPrefix', '');
  setValue('idNumber', row.shipmentId ?? '');
  setValue('shipmentQuantity', formValue(row.rawByUnit?.[unitType]?.shipmentQuantity));
  setValue('shipmentQuantityUnit', unit?.unit ?? '');
  setValue('dateOfDispatch', row.dateOfDispatchRaw ?? '');
  setValue('vesselName', row.vesselName ?? '');
  setValue('transportName', row.transportName ?? '');
};

export const SELECT_OUTGOING_SHIPMENT_CONFIG = Object.freeze({
  // Where the section sits
  name: 'selectOutgoingShipment',

  // The button and the modal's chrome
  titleKey: 'ORDERS_FULFILL_SELECT_OUTGOING_SHIPMENT',
  buttonKey: 'ORDERS_FULFILL_SELECT_OUTGOING_SHIPMENT',
  promptKey: 'ORDERS_FULFILL_LINK_EXISTING_SHIPMENT',
  emptyKey: 'ORDERS_FULFILL_OUTGOING_SHIPMENTS_EMPTY',
  selectMode: 'single',
  filterable: true,

  // The modal's own table
  buildColumns: buildSelectOutgoingShipmentColumns,
  unitOptions: buildUnitHeaderOptions,
  buildDiscrepancy,
  buildGroups,
  getRowGroup: getLinkedGroup,

  // The picked rows
  buildSelectedColumns: buildSelectedOutgoingShipmentColumns,

  // The section's own data
  useCandidates,
  writeSelection,
});
